import { Slot, Slottable } from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { DISABLED, FOCUS_RING, MOTION_COLORS, TOUCH_TARGET } from '../internal/classes';
import { cx } from '../internal/cx';
import { Spinner } from './Spinner';

/** How much weight the action carries. */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/** Control height, following the density step in `data-density`. */
export type ButtonSize = 'sm' | 'md' | 'lg';

const BASE = cx(
  'inline-flex items-center justify-center rounded-sm whitespace-nowrap select-none',
  'cursor-pointer',
  FOCUS_RING,
  MOTION_COLORS,
  TOUCH_TARGET,
);

const VARIANT_CLASSES: Readonly<Record<ButtonVariant, string>> = {
  primary: 'bg-brand text-text-on-brand hover:bg-(--ui-brand-hover)',
  secondary: 'border border-border-strong bg-surface text-text hover:bg-surface-sunken',
  ghost: 'text-text hover:bg-surface-sunken',
  // `--ui-danger-strong` is the `danger` role darkened until its label reaches 4.5:1; see styles.css.
  danger: 'bg-(--ui-danger-strong) text-text-on-brand hover:bg-(--ui-danger-strong-hover)',
};

const SIZE_CLASSES: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-(--ui-control-sm) gap-1 px-2 text-caption-strong',
  md: 'h-(--ui-control-md) gap-1.5 px-3 text-subhead-strong',
  lg: 'h-(--ui-control-lg) gap-2 px-4 text-body-strong',
};

/** Props of {@link Button}. */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** How much weight the action carries; `secondary` by default. */
  variant?: ButtonVariant;
  /** Control height; `md` by default. */
  size?: ButtonSize;
  /**
   * Shows a spinner in place of `startIcon`, marks the button busy and blocks further clicks. The
   * label stays visible, so the button does not change width.
   */
  loading?: boolean;
  /** Decorative element before the label, usually an `<Icon>`. */
  startIcon?: ReactNode;
  /** Decorative element after the label, usually an `<Icon>` or a `<Kbd>`. */
  endIcon?: ReactNode;
  /** Stretches the button to the width of its container. */
  fullWidth?: boolean;
  /**
   * Renders the single child element instead of a `<button>`, keeping the styling and the props.
   * Use it for links (`<Button asChild><a href="…">…</a></Button>`); the child then carries
   * `aria-disabled` rather than `disabled`, which only form controls understand.
   */
  asChild?: boolean;
  /** Ref to the rendered element. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * The standard action control.
 *
 * @example <Button variant="primary" onClick={send}>{t('compose.send')}</Button>
 * @example <Button asChild variant="ghost"><a href={helpUrl}>{t('common.help')}</a></Button>
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  startIcon,
  endIcon,
  fullWidth = false,
  asChild = false,
  className,
  children,
  disabled = false,
  type,
  ...rest
}: ButtonProps) {
  const inactive = disabled || loading;
  const classes = cx(
    BASE,
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[size],
    fullWidth && 'w-full',
    asChild ? 'aria-disabled:cursor-not-allowed aria-disabled:opacity-50' : DISABLED,
    className,
  );

  if (asChild) {
    return (
      <Slot
        className={classes}
        aria-busy={loading || undefined}
        aria-disabled={inactive || undefined}
        {...rest}
      >
        {loading ? <Spinner size="sm" /> : startIcon}
        <Slottable>{children}</Slottable>
        {endIcon}
      </Slot>
    );
  }

  return (
    <button
      type={type ?? 'button'}
      className={classes}
      disabled={inactive}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : startIcon}
      <Slottable>{children}</Slottable>
      {endIcon}
    </button>
  );
}
